import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * The languages the interface is written in.
 *
 * <p>Strings are written in place as {@code tx('English', 'Türkçe', ...)} rather than looked up by
 * key: the alternatives next to their use are easier to keep right than parallel dictionaries, and
 * nothing can go missing. A call may supply fewer translations than there are languages - the
 * missing ones read the English - so a language can be switched on before every string in it
 * exists, and a string is never blank for want of a translation.
 *
 * <p>The order below is the order the translations are written in at a call site. The language is
 * module state so plain functions (formatters, the API client) can read it; changing it remounts
 * the app, which re-renders every string and refetches the report in the new language.
 */
export const LANGS = ['en', 'tr', 'pl', 'pt', 'es'] as const;

export type Lang = (typeof LANGS)[number];

/** BCP 47 locales, for Intl and for the html lang attribute. */
const LOCALES: Record<Lang, string> = {
  en: 'en-GB',
  tr: 'tr-TR',
  pl: 'pl-PL',
  pt: 'pt-PT',
  es: 'es-ES',
};

const STORAGE_KEY = 'scanhome.lang';

function isLang(value: string | null): value is Lang {
  return value !== null && (LANGS as readonly string[]).includes(value);
}

function detect(): Lang {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) {
      return saved;
    }
  } catch {
    // Storage can be unavailable (private mode); fall back to the browser language.
  }
  const languages = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language];
  for (const language of languages) {
    const code = language?.toLowerCase().split('-')[0] ?? '';
    if (isLang(code)) {
      return code;
    }
  }
  return 'en';
}

let current: Lang = typeof window === 'undefined' ? 'en' : detect();

export function getLang(): Lang {
  return current;
}

/**
 * The string for the current language.
 *
 * @param english      the source string, always required
 * @param translations the other languages in {@link LANGS} order, as many as exist
 */
export function tx(english: string, ...translations: Array<string | undefined>): string {
  const index = LANGS.indexOf(current) - 1;
  if (index < 0) {
    return english;
  }
  const translated = translations[index];
  return translated === undefined || translated === '' ? english : translated;
}

/** BCP 47 locale for Intl formatting. */
export function locale(): string {
  return LOCALES[current];
}

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageContextValue>({ lang: current, setLang: () => undefined });

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(current);

  useEffect(() => {
    document.documentElement.lang = LOCALES[lang];
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    current = next;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not remembered, but still applied for this visit.
    }
    setLangState(next);
  }, []);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);

  return (
    <LanguageContext.Provider value={value}>
      <Fragment key={lang}>{children}</Fragment>
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
